import {BookOpen, Zap, TrendingUp, Trophy, Mic, Crown, ArrowRight, CheckCircle, Sparkles} from 'lucide-react'
import { useNavigate } from 'react-router-dom'

export default function LandingPage() {
  const navigate = useNavigate()

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 opacity-10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-32">
          <div className="text-center">
            <div className="mb-8 flex justify-center">
              <div className="w-24 h-24 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-3xl flex items-center justify-center shadow-2xl transform hover:scale-105 transition-transform">
                <BookOpen className="w-12 h-12 text-white" />
              </div>
            </div>
            
            <h1 className="text-5xl sm:text-7xl font-extrabold text-gray-900 mb-6 leading-tight">
              英単語学習を<br />
              <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                もっと楽しく
              </span>
            </h1>
            
            <p className="text-xl sm:text-2xl text-gray-600 mb-12 max-w-3xl mx-auto leading-relaxed">
              中学から高校まで、あなたのペースで。<br />
              音声認識とゲーミフィケーションで、継続的な学習をサポート。
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/')}
                className="group bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-bold py-5 px-10 rounded-2xl transition-all shadow-xl hover:shadow-2xl transform hover:scale-105 flex items-center justify-center"
              >
                今すぐ始める
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                onClick={() => {
                  document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })
                }}
                className="bg-white hover:bg-gray-50 text-indigo-600 font-bold py-5 px-10 rounded-2xl transition-all shadow-lg hover:shadow-xl border-2 border-indigo-200"
              >
                詳しく見る
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              充実の学習機能
            </h2>
            <p className="text-xl text-gray-600">
              あなたの英語力を最大限に引き出す、7つの特徴
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="group p-8 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl hover:shadow-2xl transition-all transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <BookOpen className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">学年別学習</h3>
              <p className="text-gray-600 leading-relaxed">
                中学1年から高校3年まで、学年に合わせた英単語を効率的に学習。自分のレベルに合った学習が可能です。
              </p>
            </div>

            {/* Feature 2 */}
            <div className="group p-8 bg-gradient-to-br from-purple-50 to-pink-50 rounded-3xl hover:shadow-2xl transition-all transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Mic className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">音声認識練習</h3>
              <p className="text-gray-600 leading-relaxed">
                発音をリアルタイムでチェック。音声入力で正しい発音を身につけ、リスニング力も同時に向上させます。
              </p>
            </div>

            {/* Feature 3 */}
            <div className="group p-8 bg-gradient-to-br from-green-50 to-emerald-50 rounded-3xl hover:shadow-2xl transition-all transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Zap className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">間隔反復学習</h3>
              <p className="text-gray-600 leading-relaxed">
                科学的に証明された記憶定着法。最適なタイミングで復習し、長期記憶に確実に定着させます。
              </p>
            </div>

            {/* Feature 4 */}
            <div className="group p-8 bg-gradient-to-br from-orange-50 to-red-50 rounded-3xl hover:shadow-2xl transition-all transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <TrendingUp className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">詳細な進捗管理</h3>
              <p className="text-gray-600 leading-relaxed">
                学習状況をグラフで可視化。週次・月次の達成状況を確認し、モチベーションを維持できます。
              </p>
            </div>

            {/* Feature 5 */}
            <div className="group p-8 bg-gradient-to-br from-yellow-50 to-amber-50 rounded-3xl hover:shadow-2xl transition-all transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-amber-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Trophy className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">母が進捗管理</h3>
              <p className="text-gray-600 leading-relaxed">
                親御さんのスマホでリアルタイムに進捗を管理できます。
              </p>
            </div>

            {/* Feature 6 */}
            <div className="group p-8 bg-gradient-to-br from-cyan-50 to-blue-50 rounded-3xl hover:shadow-2xl transition-all transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Crown className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">コイン報酬</h3>
              <p className="text-gray-600 leading-relaxed">
                学習10回ごとにコイン獲得。仮想通貨のように貯めて、今後の特典と交換できる楽しみも。
              </p>
            </div>

            {/* Feature 7 */}
            <div className="group p-8 bg-gradient-to-br from-pink-50 to-rose-50 rounded-3xl hover:shadow-2xl transition-all transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-rose-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">前置詞・慣用句・句動詞学習</h3>
              <p className="text-gray-600 leading-relaxed">
                英単語だけでなく、前置詞の使い方、慣用句、句動詞も学習可能。音声認識で実践的な英語表現を習得できます。（プレミアム限定）
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-gradient-to-br from-indigo-50 to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              シンプルな料金プラン
            </h2>
            <p className="text-xl text-gray-600">
              あなたに合ったプランを選んで、今すぐ学習を始めましょう
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* 単品プラン */}
            <div className="bg-white p-10 rounded-3xl shadow-xl hover:shadow-2xl transition-shadow border-2 border-gray-200">
              <div className="text-center mb-8">
                <h3 className="text-3xl font-bold text-gray-900 mb-4">単品プラン</h3>
                <div className="text-5xl font-extrabold text-indigo-600 mb-2">
                  ¥1,000
                  <span className="text-xl text-gray-600 font-normal">/月</span>
                </div>
                <p className="text-gray-600">このアプリのみ利用可能</p>
              </div>
              
              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">全学年の単語学習（中1〜高3）</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">前置詞・慣用句・句動詞学習</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">音声認識機能</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">詳細な進捗管理</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">全国ランキング参加</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">コイン報酬システム</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">保護者向け管理画面</span>
                </li>
              </ul>
              
              <button
                onClick={() => navigate('/')}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-4 px-6 rounded-xl transition-colors shadow-lg hover:shadow-xl"
              >
                単品プランで始める
              </button>
            </div>

            {/* フルパックプラン */}
            <div className="relative bg-gradient-to-br from-indigo-600 to-purple-600 p-10 rounded-3xl shadow-2xl hover:shadow-3xl transition-shadow">
              <div className="absolute top-0 right-0 bg-yellow-400 text-gray-900 font-bold py-2 px-6 rounded-bl-2xl rounded-tr-2xl">
                人気No.1
              </div>
              
              <div className="text-center mb-8">
                <h3 className="text-3xl font-bold text-white mb-4">フルパック</h3>
                <div className="text-5xl font-extrabold text-white mb-2">
                  ¥3,000
                  <span className="text-xl text-indigo-200 font-normal">/月</span>
                </div>
                <p className="text-indigo-200">全アプリ利用可能</p>
              </div>
              
              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-yellow-300 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-white font-semibold">英単語学習（全機能）</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-yellow-300 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-white font-semibold">前置詞・慣用句・句動詞学習</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-yellow-300 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-white font-semibold">今後追加される全アプリ</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-yellow-300 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-white font-semibold">優先サポート</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-yellow-300 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-white font-semibold">全機能フルアクセス</span>
                </li>
              </ul>
              
              <button
                onClick={() => navigate('/')}
                className="w-full bg-white hover:bg-gray-100 text-indigo-600 font-bold py-4 px-6 rounded-xl transition-colors shadow-lg hover:shadow-xl"
              >
                フルパックで始める
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            今すぐ始めて、<br />
            英語力を飛躍的に向上させよう
          </h2>
          <p className="text-xl text-gray-600 mb-10">
            登録は無料。わずか30秒で学習をスタートできます。
          </p>
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-bold py-5 px-12 rounded-2xl transition-all shadow-xl hover:shadow-2xl transform hover:scale-105"
          >
            無料で始める
            <ArrowRight className="ml-2 w-6 h-6" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-6">
            <div className="inline-flex w-16 h-16 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-2xl items-center justify-center mb-4">
              <BookOpen className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-2xl font-bold">英単語学習ソフト</h3>
          </div>
          <p className="text-gray-400 mb-6">
            効果的に英単語を学習し、目標達成をサポートします
          </p>
          <div className="text-gray-500 text-sm">
            © 2024 英単語学習ソフト. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  )
}
